"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAP_API } from "../../constants/apis";
import useAgentLiveLocations from "../../hooks/useAgentLiveLocations";
import StaffList from "./StaffList";
import MapView from "./map/MapView";
import { LocationOn } from "@mui/icons-material";

const FieldTracker = () => {
  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: GOOGLE_MAP_API,
      libraries: ["places"],
    }),
    []
  );

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);

  const { staffList, isLoading, error, connectionState, reload } = useAgentLiveLocations();
  const [selectedStaffId, setSelectedStaffId] = useState("all");

  const selectedStaff =
    selectedStaffId === "all"
      ? { id: "all", name: "All Agents" }
      : staffList.find((s) => s.id === selectedStaffId) || null;

  // Google calls this when the API key is rejected (referrer restriction, billing, API not enabled).
  const [authFailed, setAuthFailed] = useState(false);
  useEffect(() => {
    window.gm_authFailure = () => setAuthFailed(true);
    return () => {
      delete window.gm_authFailure;
    };
  }, []);

  if (loadError) {
    return (
      <Box p={3}>
        <Alert severity="error">Google Maps failed to load: {String(loadError.message || loadError)}</Alert>
      </Box>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading Field Tracker...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="sm:max-h-screen">
      {/* Header */}
      <Box className="px-6 pb-4">
        <Box className="flex items-center gap-3 mb-2">
          <Box
            className="flex items-center justify-center w-12 h-12 rounded-xl"
            sx={{
              background: "#56C662",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
            }}
          >
            <LocationOn sx={{ color: "white", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{
                background: "#56C662",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Field Operations Tracker
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor live positions and view daily routes in real-time. Click a staff member to view detailed location information.
            </Typography>
            <Chip
              size="small"
              sx={{ mt: 1 }}
              color={
                connectionState === "connected"
                  ? "success"
                  : connectionState === "reconnecting" || connectionState === "connecting"
                    ? "warning"
                    : "error"
              }
              label={
                connectionState === "connected"
                  ? "Live"
                  : connectionState === "disconnected"
                    ? "Disconnected"
                    : "Connecting…"
              }
            />
          </Box>
        </Box>
      </Box>

      {authFailed && (
        <Box className="px-6 pb-2">
          <Alert severity="error">
            Google rejected the Maps API key. Check the key's allowed referrers (add http://localhost:3000/*),
            that billing is enabled, and that "Maps JavaScript API" is enabled.
          </Alert>
        </Box>
      )}

      {error && (
        <Box className="px-6 pb-2">
          <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>
            Unable to load agent locations.
          </Alert>
        </Box>
      )}

      {/* Main Content */}
      <Box className="px-2">
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} mt={2}>
          {/* Left: Staff List */}
          <StaffList
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onSelect={(id) => setSelectedStaffId(id)}
          />

          {/* Right: Map */}
          <MapView selectedStaff={selectedStaff} staffList={staffList} />
        </Stack>
      </Box>
    </Box>
  );
};

export default FieldTracker;
